import { useCallback, useState } from "react";
import { Button, Container, Group, Paper, Progress, Stack, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconRefresh, IconTrash } from "@tabler/icons-react";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

export default function AdminMeilisearchReindexPage() {
  const [running, setRunning] = useState(false);
  const [pruneRunning, setPruneRunning] = useState(false);
  const [totalInDatabase, setTotalInDatabase] = useState<number | null>(null);
  const [scanned, setScanned] = useState(0);
  const [indexed, setIndexed] = useState(0);
  const [removedFromIndex, setRemovedFromIndex] = useState(0);
  const [pruneScanned, setPruneScanned] = useState(0);
  const [pruneDeleted, setPruneDeleted] = useState(0);
  const [pruneIndexTotal, setPruneIndexTotal] = useState<number | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  const progressPct =
    totalInDatabase != null && totalInDatabase > 0
      ? Math.min(100, Math.round((scanned / totalInDatabase) * 100))
      : 0;

  const runFullReindex = useCallback(async () => {
    if (running) return;
    setRunning(true);
    setLastError(null);
    setScanned(0);
    setIndexed(0);
    setRemovedFromIndex(0);
    setTotalInDatabase(null);

    let skip = 0;
    let cumulativeIndexed = 0;
    let cumulativeRemoved = 0;
    let cumulativeScanned = 0;
    let dbTotal: number | null = null;

    try {
      while (true) {
        const step = await trpc.admin.reindexMeilisearchCompaniesStep.mutate({ skip });
        dbTotal = step.totalInDatabase;
        setTotalInDatabase(step.totalInDatabase);
        cumulativeScanned += step.pageScanned;
        cumulativeIndexed += step.pageIndexed;
        cumulativeRemoved += step.pageRemovedFromIndex;
        setScanned(cumulativeScanned);
        setIndexed(cumulativeIndexed);
        setRemovedFromIndex(cumulativeRemoved);

        if (step.done) break;
        skip = step.nextSkip;
      }

      notifications.show({
        title: "Meilisearch sync complete",
        message: `Scanned ${cumulativeScanned} rows · indexed ${cumulativeIndexed} · removed from index ${cumulativeRemoved}`,
        color: "green",
      });
    } catch (err) {
      handleTrpcError(err);
      const msg =
        (err as { shape?: { message?: string } })?.shape?.message ??
        (err instanceof Error ? err.message : null) ??
        "Request failed";
      setLastError(msg);
    } finally {
      setRunning(false);
      if (dbTotal != null) setTotalInDatabase(dbTotal);
    }
  }, [running]);

  const runPruneOrphans = useCallback(async () => {
    if (running || pruneRunning) return;
    setPruneRunning(true);
    setLastError(null);
    setPruneScanned(0);
    setPruneDeleted(0);
    setPruneIndexTotal(null);

    let offset = 0;
    let cumulativeScanned = 0;
    let cumulativeDeleted = 0;
    let indexTotal: number | null = null;

    try {
      while (true) {
        const step = await trpc.admin.pruneMeilisearchCompanyOrphansStep.mutate({ offset });
        indexTotal = step.totalInIndex;
        setPruneIndexTotal(step.totalInIndex);
        cumulativeScanned += step.pageScanned;
        cumulativeDeleted += step.pageDeleted;
        setPruneScanned(cumulativeScanned);
        setPruneDeleted(cumulativeDeleted);

        if (step.done) break;
        offset = step.nextOffset;
      }

      notifications.show({
        title: "Orphan cleanup complete",
        message: `Scanned ${cumulativeScanned} index documents · removed ${cumulativeDeleted} stale entries`,
        color: "green",
      });
    } catch (err) {
      handleTrpcError(err);
      const msg =
        (err as { shape?: { message?: string } })?.shape?.message ??
        (err instanceof Error ? err.message : null) ??
        "Request failed";
      setLastError(msg);
    } finally {
      setPruneRunning(false);
      if (indexTotal != null) setPruneIndexTotal(indexTotal);
    }
  }, [running, pruneRunning]);

  const pruneProgressPct =
    pruneIndexTotal != null && pruneIndexTotal > 0
      ? Math.min(100, Math.round((pruneScanned / pruneIndexTotal) * 100))
      : 0;

  return (
    <Container size="sm" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2}>Search index (Meilisearch)</Title>
          <Text c="dimmed" size="sm" mt="xs">
            Reconcile PostgreSQL companies with Meilisearch. Only <strong>VERIFIED</strong> companies that are{" "}
            <strong>not admin-blocked</strong> are indexed; others are removed from the search index.
          </Text>
          <Text c="dimmed" size="sm" mt="sm">
            If search shows <strong>more</strong> companies than the database browse view, the index likely has{" "}
            <strong>orphan documents</strong> (e.g. deleted DB rows). Run <em>Remove index orphans</em> after a normal
            sync, or whenever counts diverge.
          </Text>
        </div>

        <Paper withBorder p="md" radius="md">
          <Stack gap="md">
            <Button
              leftSection={<IconRefresh size={18} />}
              onClick={() => void runFullReindex()}
              loading={running}
              disabled={running || pruneRunning}
            >
              {running ? "Syncing…" : "Sync all companies to Meilisearch"}
            </Button>

            <Button
              variant="light"
              color="orange"
              leftSection={<IconTrash size={18} />}
              onClick={() => void runPruneOrphans()}
              loading={pruneRunning}
              disabled={running || pruneRunning}
            >
              {pruneRunning ? "Removing orphans…" : "Remove index orphans (Meili → DB check)"}
            </Button>

            {totalInDatabase != null ? (
              <>
                <div>
                  <Group justify="space-between" mb={6}>
                    <Text size="sm" fw={500}>
                      Progress
                    </Text>
                    <Text size="sm" c="dimmed">
                      {scanned} / {totalInDatabase} rows
                    </Text>
                  </Group>
                  <Progress value={progressPct} size="lg" radius="md" animated={running} />
                </div>
                <Group gap="xl">
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Indexed this run
                    </Text>
                    <Text size="lg" fw={600}>
                      {indexed}
                    </Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Removed from index
                    </Text>
                    <Text size="lg" fw={600}>
                      {removedFromIndex}
                    </Text>
                  </div>
                </Group>
              </>
            ) : !running ? (
              <Text size="sm" c="dimmed">
                Run a sync to see progress. Large databases may take several minutes; keep this tab open.
              </Text>
            ) : null}

            {pruneIndexTotal != null ? (
              <>
                <div>
                  <Group justify="space-between" mb={6}>
                    <Text size="sm" fw={500}>
                      Orphan prune progress
                    </Text>
                    <Text size="sm" c="dimmed">
                      {pruneScanned} / {pruneIndexTotal} index documents
                    </Text>
                  </Group>
                  <Progress value={pruneProgressPct} size="sm" radius="md" animated={pruneRunning} color="orange" />
                </div>
                <Text size="sm" c="dimmed">
                  Stale entries removed this run: <strong>{pruneDeleted}</strong>
                </Text>
              </>
            ) : null}

            {lastError ? (
              <Text size="sm" c="red">
                {lastError}
              </Text>
            ) : null}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
